import React, { forwardRef, Ref, useImperativeHandle } from 'react';
import { WrappedFormUtils } from '@ant-design/compatible/lib/form/Form';
import FormListTabPane from './FormListTabPane';
import DraggableTabs from '../../Tabs/DraggableTabs';
import Button from '../../Button';
import { asyncSimpleModal, getRandomNumber } from '../../../lib/common';
import { NullableValue } from '../../../types/common/util';
import { FormListTabStyle } from './style';

type FieldError = {
  //에러가 발생한 필드의 name값
  field: string;
  //해당 필드 rule에서 설정한 message
  message: string;
};

//Antd 내부에서 콜백으로 전달하는 값이지만 Antd에서는 any로 정의되어 있어서 직접 타입 정의
type FormField = {
  dirty: boolean;
  errors: FieldError[];
  name: string;
  touched: boolean;
  validating: false;
  value: unknown;
};

export type TabFormFields<FieldsValue> = { [K in keyof FieldsValue]?: FormField };

export type TabItem = {
  id: number;
  title: string;
  content: (form: WrappedFormUtils) => JSX.Element;
};

interface FormListTabProps<FieldsValue extends { id: number }> {
  value?: FieldsValue[];
  onChange?: (value: FieldsValue[]) => void;
  onChangeActiveKey: (key: number) => void;
  //탭 추가시 초기값 주입하기 위한 값
  initialTabItemValue?: Partial<NullableValue<FieldsValue>>;
  canAdd?: boolean;
  activeKey?: number;
  items: (value: FieldsValue[]) => TabItem[];
  notice?: JSX.Element;
  max?: number;
}

const FormListTabInner = <FieldsValue extends { id: number }>(
  props: FormListTabProps<FieldsValue>,
  ref: Ref<{ value?: FieldsValue[]; onChange?: (value: FieldsValue[]) => void }>,
): JSX.Element => {
  const {
    value = [],
    initialTabItemValue = {},
    onChange,
    onChangeActiveKey,
    canAdd = true,
    activeKey,
    items,
    notice,
    max,
  } = props;
  useImperativeHandle(ref, () => ({
    value,
    onChange,
  }));

  const handleAddTab = async (): Promise<void> => {
    if (max && value.length >= max) {
      await asyncSimpleModal({
        type: 'error',
        title: '아이템 개수 초과',
        message: `아이템 개수는 최대 ${max}개 입니다.`,
      });
      return;
    }
    const newId = getRandomNumber();
    const newItem = { id: newId, ...initialTabItemValue };

    onChange && onChange([...value, newItem as FieldsValue]);
    onChangeActiveKey(newId);
  };

  const handleDeleteTab = async (
    targetId: string | React.MouseEvent<HTMLElement>,
    action: 'remove' | 'add',
  ): Promise<void> => {
    if (action === 'add') return;
    const confirmResult = await asyncSimpleModal({
      type: 'confirm',
      title: '아이템 삭제',
      message: '해당 아이템을 삭제합니다.\n 계속 진행하시겠습니까?',
    });

    if (!confirmResult) return;

    const nextValue = value.filter((item) => item.id !== Number(targetId));
    onChangeActiveKey(nextValue[nextValue.length - 1]?.id);
    onChange && onChange(nextValue);
  };

  const handleDrag = (newList: FieldsValue[]): void => {
    onChange && onChange(newList);
  };

  // items를 antd v5 형식으로 변환
  const tabItems = items(value)
    .map((item) => {
      const targetValue = value.find((valueItem) => valueItem.id === item.id);

      if (!targetValue) return null;

      const handleChange = (newValue: FieldsValue): void => {
        onChange && onChange(value.map((valueItem) => (valueItem.id === item.id ? newValue : valueItem)));
      };

      return {
        key: String(item.id),
        label: item.title,
        forceRender: true,
        children: <FormListTabPane id={item.id} value={targetValue} content={item.content} onChange={handleChange} />,
      };
    })
    .filter((item): item is NonNullable<typeof item> => item !== null);

  return (
    <FormListTabStyle>
      {canAdd && <Button className="add-button" color="light" text="+탭 추가" onClick={handleAddTab} type="button" />}
      {notice}
      <DraggableTabs
        hideAdd
        type="editable-card"
        dataList={value}
        list={value}
        items={tabItems}
        canDrag={true}
        activeKey={activeKey && String(activeKey)}
        onChange={(key: string): void => onChangeActiveKey(Number(key))}
        onEdit={handleDeleteTab}
        onDragEnd={handleDrag}
      />
    </FormListTabStyle>
  );
};

const FormListTab = forwardRef(FormListTabInner) as <FieldsValue extends { id: number }>(
  props: FormListTabProps<FieldsValue>,
  ref: Ref<{ value?: FieldsValue[]; onChange?: (value: FieldsValue[]) => void }>,
) => JSX.Element;

export default FormListTab;
